import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Bot, Sparkles } from "lucide-react";
import { AI_MENTOR_GROUPS } from "../components/chat/mentorCatalog";

const Page = styled.main`
  min-height: 100vh;
  padding: 116px 20px 80px;
  color: #e5e7eb;
  background:
    radial-gradient(820px at 14% 8%, rgba(245, 158, 11, 0.12), transparent 60%),
    radial-gradient(860px at 84% 12%, rgba(56, 189, 248, 0.14), transparent 62%),
    radial-gradient(900px at 50% 100%, rgba(34, 197, 94, 0.06), transparent 68%),
    #020617;
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const Hero = styled.section`
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  font-size: clamp(38px, 5vw, 56px);
  line-height: 1.08;
  font-weight: 1000;
  color: #f8fafc;
`;

const Sub = styled.p`
  margin: 18px 0 0;
  max-width: 760px;
  color: #94a3b8;
  font-size: 18px;
  line-height: 1.9;
`;

const SectionGrid = styled.div`
  margin-top: 28px;
  display: grid;
  gap: 18px;
`;

const MentorSection = styled.section`
  padding: 20px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const MentorSectionTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: 1.1rem;
`;

const MentorSectionSub = styled.p`
  margin: 8px 0 0;
  color: #94a3b8;
  font-size: 14px;
`;

const TeacherGrid = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const TeacherCard = styled.div`
  padding: 16px;
  border-radius: 20px;
  background: rgba(2, 6, 23, 0.44);
  border: 1px solid rgba(148, 163, 184, 0.12);
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;

  @media (max-width: 640px) {
    grid-template-columns: auto minmax(0, 1fr);
  }
`;

const TeacherAvatar = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  color: #04111f;
`;

const TeacherName = styled.div`
  color: #f8fafc;
  font-size: 15px;
  font-weight: 900;
`;

const TeacherDesc = styled.div`
  margin-top: 4px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.6;
`;

const StartChatBtn = styled.button`
  min-height: 42px;
  padding: 0 16px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  color: #07111f;
  font-weight: 900;
  cursor: pointer;

  @media (max-width: 640px) {
    grid-column: 1 / -1;
    width: 100%;
    margin-top: 4px;
  }
`;

export default function AiMentorsPage() {
  const navigate = useNavigate();

  return (
    <Page>
      <Container>
        <Hero>
          <Eyebrow>
            <Sparkles size={14} />
            Mentor directory
          </Eyebrow>
          <Title>AI Mentors for your community workflow</Title>
          <Sub>
            Choose a mentor based on what you want to improve right now:
            skills, role clarity, interview prep, project execution, or core
            subject understanding.
          </Sub>
        </Hero>

        <SectionGrid>
          {AI_MENTOR_GROUPS.map((group) => (
            <MentorSection key={group.title}>
              <MentorSectionTitle>{group.title}</MentorSectionTitle>
              <MentorSectionSub>{group.subtitle}</MentorSectionSub>

              <TeacherGrid>
                {group.mentors.map((mentor) => (
                  <TeacherCard key={mentor.id}>
                    <TeacherAvatar>
                      <Bot size={18} />
                    </TeacherAvatar>

                    <div>
                      <TeacherName>{mentor.name}</TeacherName>
                      <TeacherDesc>{mentor.desc}</TeacherDesc>
                    </div>

                    <StartChatBtn onClick={() => navigate(`/ai-mentor-chat/${mentor.id}`)}>
                      Start Chat
                    </StartChatBtn>
                  </TeacherCard>
                ))}
              </TeacherGrid>
            </MentorSection>
          ))}
        </SectionGrid>
      </Container>
    </Page>
  );
}
